import React, { useState } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import DeploymentStatus from '../components/DeploymentStatus'

const { FiRocket, FiServer, FiGlobe, FiSettings, FiDatabase, FiShield } = FiIcons

const DeploymentDashboard = () => {
  const [activeTab, setActiveTab] = useState('status')

  const tabs = [
    { id: 'status', name: 'Deployment Status', icon: FiRocket },
    { id: 'database', name: 'Database', icon: FiDatabase },
    { id: 'hosting', name: 'Hosting', icon: FiServer },
    { id: 'domain', name: 'Domain & DNS', icon: FiGlobe },
    { id: 'security', name: 'Security', icon: FiShield },
    { id: 'settings', name: 'Settings', icon: FiSettings }
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'status':
        return <DeploymentStatus />
      
      case 'database':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Database Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center space-x-3 mb-4">
                  <SafeIcon icon={FiDatabase} className="h-6 w-6 text-green-600" />
                  <h4 className="font-semibold text-green-900">Supabase Connected</h4>
                </div>
                <p className="text-green-700 text-sm mb-3">
                  Database URL: https://ulmdmgufrfvriyzezbnk.supabase.co
                </p>
                <p className="text-green-700 text-sm">
                  All tables created and configured with RLS policies.
                </p>
              </div>
              <div className="p-6 bg-blue-50 border border-blue-200 rounded-xl">
                <h4 className="font-semibold text-blue-900 mb-3">Tables Created:</h4>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>✅ blog_posts_ai2024</li>
                  <li>✅ case_studies_ai2024</li>
                  <li>✅ contact_messages_ai2024</li>
                  <li>✅ media_files_ai2024</li>
                  <li>✅ authors_ai2024</li>
                </ul>
              </div>
            </div>
          </div>
        )
      
      case 'hosting':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Hosting Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-white border border-gray-200 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-3">Vercel (Recommended)</h4>
                <ul className="text-gray-600 text-sm space-y-2 mb-4">
                  <li>✅ Automatic deployments from Git</li>
                  <li>✅ Global CDN</li>
                  <li>✅ Automatic SSL</li>
                  <li>✅ Preview deployments</li>
                </ul>
                <a
                  href="https://vercel.com/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Deploy to Vercel
                </a>
              </div>
              <div className="p-6 bg-white border border-gray-200 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-3">Netlify</h4>
                <ul className="text-gray-600 text-sm space-y-2 mb-4">
                  <li>✅ Drag & drop deployment</li>
                  <li>✅ Form handling</li>
                  <li>✅ Branch previews</li>
                  <li>✅ Analytics</li>
                </ul>
                <a
                  href="https://app.netlify.com/start"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Deploy to Netlify
                </a>
              </div>
            </div>
          </div>
        )
      
      case 'domain':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Domain & DNS Setup</h3>
            <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
              <h4 className="font-semibold text-yellow-900 mb-3">Next Steps:</h4>
              <ol className="text-yellow-800 text-sm space-y-2">
                <li>1. Purchase domain: www.aiximo.com</li>
                <li>2. Configure DNS records to point to your hosting provider</li>
                <li>3. Set up business email: info@aiximo.com</li>
                <li>4. Enable SSL certificate (automatic with hosting)</li>
              </ol>
            </div>
          </div>
        )
      
      case 'security':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Security Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-50 border border-green-200 rounded-xl">
                <h4 className="font-semibold text-green-900 mb-3">✅ Implemented</h4>
                <ul className="text-green-700 text-sm space-y-1">
                  <li>• Row Level Security (RLS)</li>
                  <li>• Input validation</li>
                  <li>• HTTPS enforcement</li>
                  <li>• Admin authentication</li>
                  <li>• Secure file uploads</li>
                </ul>
              </div>
              <div className="p-6 bg-blue-50 border border-blue-200 rounded-xl">
                <h4 className="font-semibold text-blue-900 mb-3">📝 Recommended</h4>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Add rate limiting</li>
                  <li>• Implement CAPTCHA</li>
                  <li>• Set up monitoring</li>
                  <li>• Configure backups</li>
                  <li>• Add security headers</li>
                </ul>
              </div>
            </div>
          </div>
        )
      
      default:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Deployment Settings</h3>
            <div className="p-6 bg-white border border-gray-200 rounded-xl">
              <p className="text-gray-600">Configure deployment settings and environment variables here.</p>
            </div>
          </div>
        )
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Deployment Dashboard</h1>
        <p className="text-gray-600">Manage your deployment and go live with aiximo.com</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SafeIcon icon={tab.icon} className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {renderTabContent()}
      </motion.div>
    </div>
  )
}

export default DeploymentDashboard